import { ArrowLeft, Download, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';

import { getAppDefinition } from '@/apps';
import { LevelChips } from '@/components/home/LevelChips';
import { TierChips } from '@/components/home/TierChips';
import { ActivityHeatmap } from '@/components/stats/ActivityHeatmap';
import { MetricTiles } from '@/components/stats/MetricTiles';
import { StreakDistributionBar } from '@/components/stats/StreakDistributionBar';
import { Button } from '@/components/ui/button';
import { AppHomeSkeleton } from '@/components/ui/skeletons';
import type { CefrLevel, CoverageTier, SessionMode } from '@/core/types';
import { api } from '@/lib/api';
import { getAppIcon } from '@/lib/app-icons';
import { useApps, useAppStats } from '@/lib/queries';

export function AppHomeRoute() {
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const appsQuery = useApps();
  const statsQuery = useAppStats(appId);
  const [selectedLevels, setSelectedLevels] = useState<CefrLevel[]>([]);
  const [selectedTiers, setSelectedTiers] = useState<CoverageTier[]>([]);

  if (appsQuery.isLoading) {
    return (
      <div className="min-h-screen">
        <header className="border-border bg-card border-b">
          <div className="mx-auto h-[60px] max-w-3xl px-4 py-4 sm:px-6" />
        </header>
        <AppHomeSkeleton />
      </div>
    );
  }

  if (appsQuery.error) {
    return <CenteredMessage variant="error">{appsQuery.error.message}</CenteredMessage>;
  }

  const app = appsQuery.data?.apps.find((a) => a.id === appId);
  if (!app) {
    return <CenteredMessage variant="error">Unknown app: {appId}</CenteredMessage>;
  }
  if (app.status !== 'active') {
    return (
      <CenteredMessage variant="error">
        {app.display_name} isn't installed yet (status: {app.status}).
      </CenteredMessage>
    );
  }

  // Apps that ship a custom launcher (einbuergerungstest) render their own
  // layout instead of the generic CEFR-chip + Practice/Review/Archive UI.
  const appDef = getAppDefinition(appId!);
  if (appDef?.Home) {
    return <appDef.Home appId={appId!} />;
  }

  const appStats = app.stats!;
  const Icon = getAppIcon(app.icon);
  const strugglingCount = statsQuery.data?.struggling_count ?? 0;
  const tierProgress = statsQuery.data?.tier_progress ?? null;
  const levelProgress = statsQuery.data?.level_progress;

  // Which grouping this deck uses, if any.
  //   Coverage tiers  → catalog vocabulary decks (einbuergerungstest-vokabeln).
  //   CEFR levels     → decks with at least one levelled card.
  //   Neither         → no chip row at all.
  // The last case matters: level_progress always returns all six CEFR rows, so
  // without the `some(total > 0)` guard an unlevelled deck would render six
  // empty disabled chips promising a filter that does nothing.
  const grouping: 'tiers' | 'levels' | 'none' = tierProgress
    ? 'tiers'
    : levelProgress?.some((l) => l.total > 0)
      ? 'levels'
      : 'none';

  // Pool counts: when chips are selected, sum across them; otherwise use the
  // app-wide totals from /api/apps. Falls back to app-wide while stats load.
  const poolCounts =
    grouping === 'tiers'
      ? computePoolCounts({
          selected: selectedTiers,
          rows: tierProgress?.tiers,
          key: 'tier',
          fallback: appStats,
        })
      : computePoolCounts({
          selected: selectedLevels,
          rows: levelProgress,
          key: 'level',
          fallback: appStats,
        });

  const startPractice = (mode: SessionMode) => {
    const params =
      grouping === 'tiers'
        ? selectedTiers.length > 0
          ? `&tiers=${selectedTiers.join(',')}`
          : ''
        : selectedLevels.length > 0
          ? `&levels=${selectedLevels.join(',')}`
          : '';
    navigate(`/apps/${appId}/practice?mode=${mode}${params}`);
  };

  return (
    <div className="min-h-screen">
      <header className="border-border bg-card border-b">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-4 sm:px-6">
          <Button asChild variant="ghost" size="sm">
            <Link to="/">
              <ArrowLeft className="size-4" />
              Back
            </Link>
          </Button>
          <div className="flex items-center gap-2">
            <Icon className="text-primary size-5" />
            <h1 className="font-display text-xl font-semibold">{app.display_name}</h1>
          </div>
        </div>
      </header>

      <main className="mx-auto flex max-w-3xl flex-col gap-6 px-4 py-6 sm:px-6 sm:py-10">
        {grouping === 'tiers' && tierProgress ? (
          <section className="flex flex-col gap-2">
            <h2 className="text-muted-foreground text-sm font-medium">
              How many exam questions the word appears in
            </h2>
            <TierChips
              tierProgress={tierProgress}
              selected={selectedTiers}
              onChange={setSelectedTiers}
            />
          </section>
        ) : grouping === 'levels' && levelProgress ? (
          <section className="flex flex-col gap-2">
            <h2 className="text-muted-foreground text-sm font-medium">Levels</h2>
            <LevelChips
              levelProgress={levelProgress}
              selected={selectedLevels}
              onChange={setSelectedLevels}
            />
          </section>
        ) : null}

        <section className="flex flex-col gap-3">
          <Button
            size="lg"
            className="h-14 text-base"
            disabled={poolCounts.learning === 0}
            onClick={() => startPractice('practice')}
          >
            Practice new words · {poolCounts.learning}
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="h-14 text-base"
            disabled={poolCounts.known === 0}
            onClick={() => startPractice('review')}
          >
            Review recently memorized · {poolCounts.known}
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="h-14 text-base"
            disabled={poolCounts.archived === 0}
            onClick={() => startPractice('archive')}
          >
            Review archive · {poolCounts.archived}
          </Button>
          {/* Hidden rather than disabled when nothing is flagged: the other
              three modes always exist and are merely empty, but an app with no
              mistakes has nothing to advertise. It appears the day it applies. */}
          {strugglingCount > 0 && (
            <Button
              size="lg"
              variant="outline"
              className="border-warning/40 text-warning hover:bg-warning/10 h-14 text-base"
              onClick={() => startPractice('mistakes')}
            >
              <TriangleAlert className="size-4" />
              Practice my mistakes · {strugglingCount}
            </Button>
          )}
        </section>

        {strugglingCount > 0 && (
          <div className="text-center">
            <Link
              to={`/apps/${appId}/mistakes`}
              className="text-muted-foreground text-sm underline-offset-4 hover:underline"
            >
              See what keeps getting you →
            </Link>
          </div>
        )}

        <StatsSection statsQuery={statsQuery} />

        <div className="text-center">
          <Link
            to={`/apps/${appId}/stats`}
            className="text-primary text-sm font-medium underline-offset-4 hover:underline"
          >
            View detailed stats →
          </Link>
        </div>

        <section className="flex flex-wrap gap-3">
          <Button asChild variant="secondary">
            <a href={api.exportUrl()} download>
              <Download className="size-4" />
              Export backup
            </a>
          </Button>
        </section>
      </main>
    </div>
  );
}

type PoolCounts = { learning: number; known: number; archived: number };

/**
 * Sums the mode pools across the selected chips (or all of them when nothing
 * is selected). Works for either grouping — both row shapes carry the same
 * learning/known/archived counts, they just key on a different field.
 */
function computePoolCounts<
  TRow extends PoolCounts & Record<TKey, TValue>,
  TKey extends 'level' | 'tier',
  TValue,
>(opts: {
  selected: TValue[];
  rows: TRow[] | null | undefined;
  key: TKey;
  fallback: PoolCounts;
}): PoolCounts {
  const { selected, rows, key, fallback } = opts;
  if (!rows) return fallback;
  const filtered = selected.length === 0 ? rows : rows.filter((r) => selected.includes(r[key]));
  return filtered.reduce<PoolCounts>(
    (acc, r) => ({
      learning: acc.learning + r.learning,
      known: acc.known + r.known,
      archived: acc.archived + r.archived,
    }),
    { learning: 0, known: 0, archived: 0 },
  );
}

function StatsSection({ statsQuery }: { statsQuery: ReturnType<typeof useAppStats> }) {
  if (statsQuery.isLoading) {
    return <p className="text-muted-foreground text-sm">Loading statistics…</p>;
  }
  if (statsQuery.error || !statsQuery.data) {
    return (
      <p className="text-destructive text-sm">
        Couldn't load statistics: {statsQuery.error?.message ?? 'no data'}
      </p>
    );
  }
  const { metrics, streak_distribution, activity_heatmap } = statsQuery.data;
  return (
    <div className="flex flex-col gap-6">
      <MetricTiles {...metrics} />
      <StreakDistributionBar distribution={streak_distribution} />
      <ActivityHeatmap data={activity_heatmap} />
    </div>
  );
}

function CenteredMessage({
  children,
  variant = 'default',
}: {
  children: React.ReactNode;
  variant?: 'default' | 'error';
}) {
  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <p className={variant === 'error' ? 'text-destructive' : 'text-muted-foreground'}>
        {children}
      </p>
    </div>
  );
}
